<template>
  <div :class="['main-container', { 'sidebar-expanded': isSidebarExpanded }]">
    <!-- Sidebar -->
    <aside class="aside">
      <the-sidebar-menu :nav-menu-items="availableMenuItems"/>
    </aside>

    <div class="main-content">
      <div class="top-bar-wrapper">
        <slot name="topbar"></slot>
      </div>
      <div class="page-content-wrapper">
        <slot name="pageContent"></slot>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div class="notification-wrapper">
      <transition-group name="list" tag="div" class="notification-list">
        <mc-notification v-for="notification in notifications" :id="notification.id" :key="notification.id" :title="notification.title" :message="notification.message"></mc-notification>
      </transition-group>
    </div>
  </Teleport>
  <mc-unsaved-changes-dialog></mc-unsaved-changes-dialog>
  <mc-toast></mc-toast>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useHead, useRouter } from "nuxt/app";
import McNotification from "@/components/ui/McNotification.vue";
import McToast from "@/components/ui/McToast.vue";
import McUnsavedChangesDialog from "@/components/ui/McDialog/McUnsavedChangesDialog.vue";
import TheSidebarMenu from "@/components/shared/layout/TheSidebarMenu.vue";
import { useNotificationStore } from "@/store/notificationStore";
import { useUserProfileStore } from "@/store/userProfileStore";
import { AppPageNames, ROUTES_CONFIG } from "@/constants/appRoutes";
import { useQuickFilterStore } from "@/store/quickFilterStore";
import { NAV_MENU_ITEMS } from "@/constants/menu";
import { DEFAULT_FILTER_STATE } from "@/constants/filter";

const router = useRouter();
const quickFilterStore = useQuickFilterStore();
const userProfileStore = useUserProfileStore();
const notificationsStore = useNotificationStore();

const selectedView = computed(() => quickFilterStore.selectedQuickFilter ?? DEFAULT_FILTER_STATE);

const routeConfig = computed(() => {
  const currentRouteName = router.currentRoute.value.name?.toString().toLowerCase();
  return ROUTES_CONFIG.find((route) => route.name.toLowerCase() === currentRouteName);
});

const pageTitle = computed(() => {
  if (!routeConfig.value) return AppPageNames.unknown;
  return `${routeConfig.value.pageName}${routeConfig.value.isViewMode ? ` (${selectedView.value.name})` : ''}`;
});

const notifications = computed(() => notificationsStore.notifications);
const availableMenuItems = computed(() => NAV_MENU_ITEMS.filter((item) => !item.featureName || userProfileStore.hasFeature(item.featureName)));
const isSidebarExpanded = computed(() => userProfileStore.isSidebarExpanded);

useHead({ title: pageTitle });
</script>

<style scoped lang="scss">
.main-container {
  display: flex;
  height: 100vh;
  min-height: var(--page-min-height);
  width: 100%;
  min-width: var(--page-min-width);

  &.sidebar-expanded {
    --sidebar-width: var(--sidebar-expanded-width);
  }
}

.aside {
  max-width: var(--sidebar-width);
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: 30;
  min-height: var(--page-min-height);
}

.aside:hover {
  max-width: var(--sidebar-expanded-width);
}

.main-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  min-height: var(--page-min-height);
  padding-left: var(--sidebar-width);

  .top-bar-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .page-content-wrapper {
    height: 100%;
    overflow-x: auto;
  }
}

.notification-wrapper {
  position: fixed;
  bottom: 48px;
  right: 48px;
  z-index: 1000;

  .notification-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.list-enter-active,
.list-leave-active {
  transition: all 0.3s;
  transform: translateX(300px);
}

.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.list-enter-to,
.list-leave {
  opacity: 1;
  transform: translateX(0px);
}
</style>
